import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  WorkspaceShell, WorkspaceHeader, WorkspaceQuickLink as QuickLink,
  WorkspaceDetailField as DetailField, normalizeUrl,
} from "./workspace/WorkspaceShell";
import {
  getCandidate, updateCandidate, addCandidateNote, candidateMatches,
  getCandidateResumeBlob, deleteCandidate, candidateAssociations,
} from "@/lib/api";
import { hasPerm } from "@/lib/auth";
import {
  Loader2, FileText, Download, Sparkles, Tag as TagIcon, Target, X, Plus,
  Mail, Phone, Linkedin, ExternalLink, Star, Clock, Users, MapPin,
  Briefcase, GraduationCap, Globe, DollarSign, CheckCircle2, Building2, Trash2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Shared candidate presentation helpers (also imported by CandidatesManager so
// list + workspace stay visually consistent).
// ---------------------------------------------------------------------------
export const CANDIDATE_STATUSES = [
  "Applied", "Reviewing", "Qualified", "Interviewing", "Available",
  "Presented", "Placed", "Rejected", "Archived",
];

const SCORE_KEYS = [
  ["overall", "Overall"], ["customer_facing", "Customer-facing"], ["administrative", "Administrative"],
  ["technical", "Technical"], ["communication", "Communication"], ["leadership", "Leadership"],
];

export const candidateStatusColor = (s) => ({
  Applied: "bg-white/10 text-white/70 border-white/15",
  Reviewing: "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30",
  Qualified: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  Interviewing: "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30",
  Available: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
  Presented: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  Placed: "bg-[#2EE59D]/20 text-[#2EE59D] border-[#2EE59D]/40",
  Rejected: "bg-[#FF5C7A]/15 text-[#FF5C7A] border-[#FF5C7A]/30",
  Archived: "bg-white/5 text-white/40 border-white/10",
}[s] || "bg-white/10 text-white/70 border-white/15");

export const Stars = ({ value, onChange, readOnly }) => (
  <div className="flex gap-0.5" data-testid="candidate-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={readOnly}
        onClick={() => onChange && onChange(n)}
        className={`${readOnly ? "cursor-default" : "cursor-pointer"} rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#66B2FF]`}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}>
        <Star size={16} className={n <= value ? "fill-[#F5B94A] text-[#F5B94A]" : "text-white/25"} />
      </button>
    ))}
  </div>
);

const ScoreBar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-white/60">{label}</span>
      <span className="text-white/80 tabular-nums">{value}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full bg-[#1E6BFF]" style={{ width: `${value}%` }} />
    </div>
  </div>
);

// Match-history card (candidate vs an open Blueprint/Deal). Keyboard-accessible.
const MatchCard = ({ m, onOpen }) => {
  const title = m.company || m.industry || "Opportunity";
  const subtitle = [m.industry, (m.roles || []).slice(0, 2).join(", ")].filter(Boolean).join(" \u00b7 ");
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen && onOpen(); } }}
      className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E] cursor-pointer transition-colors hover:bg-[#141c33] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66B2FF] focus-visible:ring-offset-1 focus-visible:ring-offset-[#0E1424]"
      data-testid="candidate-match-card"
      aria-label={`${title}, ${Math.round(m.overall || 0)} percent match. Open Blueprint.`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-white truncate">{title}</div>
          {subtitle && <div className="text-white/45 text-xs truncate">{subtitle}</div>}
        </div>
        <div className="text-2xl font-bold text-[#66B2FF] tabular-nums">{Math.round(m.overall || 0)}%</div>
      </div>
      {Array.isArray(m.breakdown) && m.breakdown.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
          {m.breakdown.map((b) => (
            <div key={b.key} className="flex items-center justify-between text-xs">
              <span className="text-white/55">{b.label}</span>
              <span className="text-white/80 tabular-nums">{b.score}%</span>
            </div>
          ))}
        </div>
      )}
      {(m.strengths || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[#2EE59D] text-xs font-medium mb-1">Why they match</div>
          <div className="flex flex-wrap gap-1.5">
            {m.strengths.map((s, i) => (
              <span key={i} className="text-xs bg-[#2EE59D]/10 text-[#2EE59D] rounded-md px-2 py-0.5">{s}</span>
            ))}
          </div>
        </div>
      )}
      {(m.gaps || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[#F5B94A] text-xs font-medium mb-1">Possible gaps</div>
          <div className="flex flex-wrap gap-1.5">
            {m.gaps.map((s, i) => (
              <span key={i} className="text-xs bg-[#F5B94A]/10 text-[#F5B94A] rounded-md px-2 py-0.5">{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SectionTitle = ({ icon: Icon, children, extra }) => (
  <div className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
    {Icon && <Icon size={14} className="text-[#66B2FF]" />} {children}
    {extra}
  </div>
);

const fmtNoteDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(s).slice(0, 10); }
};

// ===========================================================================
// CandidateWorkspace — the recruiting counterpart to the Prospect Workspace.
// Reuses the shared WorkspaceShell so layout, keyboard a11y and theming match.
// ===========================================================================
export const CandidateWorkspace = ({ token, id, onClose, onChanged, onDeleted }) => {
  const canManage = hasPerm("candidates.manage");
  const canMatch = hasPerm("matches.view");

  const [c, setC] = useState(null);
  const [matches, setMatches] = useState(null);
  const [associations, setAssociations] = useState(null);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [tagText, setTagText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getCandidate(token, id);
      setC(d.candidate);
      if (d.candidate.resume_file_id) {
        getCandidateResumeBlob(token, id)
          .then((res) => setResumeUrl(URL.createObjectURL(res.data)))
          .catch(() => {});
      }
      if (canMatch) {
        candidateMatches(token, id)
          .then((m) => setMatches(m.matches || []))
          .catch(() => setMatches([]));
      } else {
        setMatches([]);
      }
      candidateAssociations(token, id)
        .then((r) => setAssociations(r.associations || []))
        .catch(() => setAssociations([]));
    } catch {
      toast.error("Failed to load candidate");
      onClose();
    }
  }, [token, id, canMatch, onClose]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Clean up the blob URL when the workspace closes / candidate changes.
  useEffect(() => () => { if (resumeUrl) URL.revokeObjectURL(resumeUrl); }, [resumeUrl]);

  const patch = async (body) => {
    setBusy(true);
    try {
      const res = await updateCandidate(token, id, body);
      setC(res.candidate);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const addTag = () => {
    const t = tagText.trim();
    if (!t) return;
    patch({ tags: [...(c.tags || []), t] });
    setTagText("");
  };
  const removeTag = (t) => patch({ tags: (c.tags || []).filter((x) => x !== t) });

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const res = await addCandidateNote(token, id, noteText.trim());
      setC((p) => ({ ...p, notes: [...(p.notes || []), res.note] }));
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Could not add note");
    } finally {
      setBusy(false);
    }
  };

  const openBlueprint = (submissionId) => submissionId && window.open(`/blueprint/${submissionId}`, "_blank");

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteCandidate(token, id);
      toast.success("Candidate deleted");
      onChanged && onChanged();
      onDeleted && onDeleted(id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const downloadResume = () => {
    if (!resumeUrl) return;
    const a = document.createElement("a");
    a.href = resumeUrl;
    a.download = c?.resume_filename || "resume";
    a.click();
  };

  const location = c ? [c.city, c.state, c.country].filter(Boolean).join(", ") : "";
  const subtitle = c
    ? [c.current_position || c.desired_positions, location].filter(Boolean).join(" \u00b7 ")
    : "";

  return (
    <WorkspaceShell
      onClose={onClose}
      loading={!c}
      ariaLabel={c ? `${c.name} — Candidate workspace` : "Candidate workspace"}
      testid="candidate-detail-panel"
      closeTestid="candidate-workspace-close-x"
      header={c && (
        <WorkspaceHeader
          title={c.name}
          badges={
            <>
              <Badge className={`border ${candidateStatusColor(c.status)}`}>{c.status}</Badge>
              {c.availability && (
                <Badge className="bg-white/10 text-white/70 border border-white/15">
                  {c.availability}
                </Badge>
              )}
            </>
          }
          subtitle={subtitle}
          actions={
            <>
              <QuickLink href={c.email ? `mailto:${c.email}` : null} icon={Mail} label="Email" testid="candidate-quick-email" disabledTip="No email address available." />
              <QuickLink href={c.phone ? `tel:${c.phone}` : null} icon={Phone} label="Call" testid="candidate-quick-call" disabledTip="No phone number available." />
              <QuickLink href={normalizeUrl(c.linkedin)} icon={Linkedin} label="LinkedIn" testid="candidate-quick-linkedin" external disabledTip="No LinkedIn profile available." />
              <QuickLink onClick={resumeUrl ? downloadResume : undefined} icon={Download} label="Resume" testid="candidate-quick-resume" disabledTip="No resume on file." />
            </>
          }
        />
      )}
      footer={c && (
        <>
          {canManage && (
            confirmDelete ? (
              <div className="mr-auto flex items-center gap-2" data-testid="candidate-delete-confirmbar">
                <span className="text-xs text-[#FF5C7A]">Delete this candidate permanently?</span>
                <Button variant="ghost" size="sm" className="text-white/60 hover:text-white h-8" onClick={() => setConfirmDelete(false)} disabled={busy} data-testid="candidate-delete-cancel">Cancel</Button>
                <Button size="sm" className="bg-[#FF5C7A] hover:bg-[#ff456a] text-white h-8" onClick={doDelete} disabled={busy} data-testid="candidate-delete-confirm">
                  {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Trash2 size={14} className="mr-1" />} Delete
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="mr-auto text-[#FF5C7A] hover:bg-[#FF5C7A]/10 h-8" onClick={() => setConfirmDelete(true)} disabled={busy} data-testid="candidate-delete">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )
          )}
          <span className={`text-white/35 text-xs hidden sm:inline ${canManage ? "" : "mr-auto"}`}>
            Changes to controls save automatically.
          </span>
          <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={onClose} disabled={busy} data-testid="candidate-workspace-done">
            {busy ? <Loader2 className="animate-spin mr-1" size={15} /> : <CheckCircle2 size={15} className="mr-1" />}
            Done
          </Button>
        </>
      )}
    >
      {c && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* LEFT (2 cols): controls, notes/timeline, AI, match history */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recruiter controls */}
            <div>
              <SectionTitle>Recruiter Controls</SectionTitle>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="text-white/45 text-xs">Status</label>
                  <Select value={c.status} onValueChange={(v) => patch({ status: v })} disabled={!canManage}>
                    <SelectTrigger className="bg-[#0A0F1E] border-white/10 h-10 text-white mt-1" data-testid="candidate-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                      {CANDIDATE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/45 text-xs">Recruiter rating (private)</label>
                  <div className="mt-2">
                    <Stars value={c.recruiter_rating || 0} onChange={canManage ? (n) => patch({ recruiter_rating: n }) : undefined} readOnly={!canManage} />
                  </div>
                </div>
                <div>
                  <label className="text-white/45 text-xs">Availability</label>
                  <div className="text-white/80 text-sm mt-2 h-10 flex items-center">{c.availability || "\u2014"}</div>
                </div>
              </div>
            </div>

            <Separator className="bg-white/10" />

            {/* Notes / activity timeline */}
            <div>
              <SectionTitle icon={Clock}>Recruiter Notes & Activity</SectionTitle>
              {canManage && (
                <div className="flex gap-2 mb-3">
                  <Input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNote(); } }}
                    placeholder="Add an internal note (private)…" data-testid="candidate-note-input"
                    className="bg-[#0A0F1E] border-white/10 h-10 text-white" />
                  <Button onClick={saveNote} disabled={busy || !noteText.trim()} data-testid="candidate-note-add"
                    className="bg-[#1E6BFF] hover:bg-[#3B82F6] h-10"><Plus size={15} /></Button>
                </div>
              )}
              {(c.notes || []).length === 0 ? (
                <div className="text-white/35 text-sm">No notes yet.</div>
              ) : (
                <div className="space-y-2">
                  {[...(c.notes || [])].reverse().map((n) => (
                    <div key={n.id} className="rounded-lg border border-white/10 p-3 text-sm bg-[#0A0F1E]" data-testid="candidate-note">
                      <div className="text-white/80">{n.text}</div>
                      <div className="text-white/35 text-xs mt-1">{n.author}{n.at ? ` \u00b7 ${fmtNoteDate(n.at)}` : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* AI assessment */}
            <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]">
              <SectionTitle icon={Sparkles}>AI Assessment</SectionTitle>
              {c.ai?.professional_summary
                ? <p className="text-white/70 text-sm mb-3">{c.ai.professional_summary}</p>
                : <p className="text-white/40 text-sm mb-3">AI summary pending.</p>}
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
                {SCORE_KEYS.map(([k, label]) => <ScoreBar key={k} label={label} value={c.ai?.scores?.[k] || 0} />)}
              </div>
              {(c.ai?.strengths || []).length > 0 && (
                <div className="mt-3 text-xs"><span className="text-[#2EE59D] font-medium">Strengths: </span><span className="text-white/60">{c.ai.strengths.join(", ")}</span></div>
              )}
              {(c.ai?.ideal_roles || []).length > 0 && (
                <div className="mt-1 text-xs"><span className="text-[#66B2FF] font-medium">Ideal roles: </span><span className="text-white/60">{c.ai.ideal_roles.join(", ")}</span></div>
              )}
            </div>

            {/* Match history / opportunities */}
            {canMatch && (
              <div>
                <SectionTitle icon={Target} extra={matches === null && <Loader2 size={13} className="animate-spin text-white/40" />}>
                  Match History &amp; Opportunities
                </SectionTitle>
                {matches === null ? (
                  <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Calculating matches&hellip;</div>
                ) : matches.length === 0 ? (
                  <div className="text-white/35 text-sm">No open Blueprints/Deals to match against yet.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="candidate-matches">
                    {matches.slice(0, 8).map((m) => (
                      <MatchCard key={m.submission_id} m={m} onOpen={() => openBlueprint(m.submission_id)} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Staffing Associations — deals/prospects this candidate is attached to */}
            <div data-testid="candidate-associations">
              <SectionTitle icon={Briefcase} extra={associations === null && <Loader2 size={13} className="animate-spin text-white/40" />}>
                Associations
              </SectionTitle>
              {associations === null ? (
                <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading&hellip;</div>
              ) : associations.length === 0 ? (
                <div className="text-white/35 text-sm">Not attached to any deals yet. Shortlist this candidate from the Staffing workflow.</div>
              ) : (
                <div className="space-y-2" data-testid="candidate-associations-list">
                  {associations.map((a) => {
                    const stageCls = {
                      Matched: "bg-white/10 text-white/70 border-white/15",
                      Shortlisted: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
                      Interviewing: "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/30",
                      Selected: "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30",
                      Placed: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
                    }[a.stage] || "bg-white/10 text-white/70 border-white/15";
                    return (
                      <div key={a.deal_id} className="rounded-lg border border-white/10 bg-[#0A0F1E] p-3 flex items-center justify-between gap-3" data-testid="candidate-association-row">
                        <div className="min-w-0">
                          <div className="text-white font-medium truncate">{a.company || "—"}</div>
                          <div className="text-white/45 text-xs truncate">
                            {a.contact || "—"}
                            {a.monthly_value ? ` \u00b7 $${Number(a.monthly_value).toLocaleString()}/mo` : ""}
                            {a.start_date ? ` \u00b7 Starts ${a.start_date}` : ""}
                          </div>
                        </div>
                        <Badge className={`border ${stageCls} shrink-0`}>{a.stage || "—"}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT (1 col): resume, tags, skills, profile info */}
          <div className="space-y-6">
            {/* Resume */}
            <div className="rounded-xl border border-white/10 overflow-hidden bg-[#0A0F1E]">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
                <span className="text-white/70 text-sm flex items-center gap-2"><FileText size={15} /> Resume</span>
                {resumeUrl && (
                  <button onClick={downloadResume} data-testid="candidate-download-resume"
                    className="text-[#66B2FF] text-xs flex items-center gap-1 hover:underline"><Download size={13} /> Download</button>
                )}
              </div>
              {resumeUrl ? (
                <iframe title="resume" src={resumeUrl} className="w-full h-64 bg-white" data-testid="candidate-resume-viewer" />
              ) : (
                <div className="text-white/40 text-sm px-4 py-6">{c.resume_file_id ? "Loading resume\u2026" : "No resume on file."}</div>
              )}
            </div>

            {/* Tags */}
            <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]">
              <SectionTitle icon={TagIcon}>Tags</SectionTitle>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(c.tags || []).map((t) => (
                  <span key={t} className="text-xs bg-[#1E6BFF]/15 text-[#66B2FF] rounded-md px-2 py-1 flex items-center gap-1" data-testid="candidate-tag">
                    {t}{canManage && <button onClick={() => removeTag(t)} aria-label={`Remove tag ${t}`}><X size={11} /></button>}
                  </span>
                ))}
                {(c.tags || []).length === 0 && <span className="text-white/40 text-sm">No tags</span>}
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Input value={tagText} onChange={(e) => setTagText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    placeholder="Add tag (e.g. High Potential)" data-testid="candidate-tag-input"
                    className="bg-[#0A0F1E] border-white/10 h-10 text-white" />
                  <Button onClick={addTag} className="bg-[#1E6BFF] hover:bg-[#3B82F6] h-10"><Plus size={15} /></Button>
                </div>
              )}
            </div>

            {/* Skills & industries */}
            <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]">
              <SectionTitle>Skills &amp; Industries</SectionTitle>
              <div className="flex flex-wrap gap-1.5">
                {(c.skills || []).length === 0 && (c.industries || []).length === 0 && (
                  <span className="text-white/40 text-sm">None listed</span>
                )}
                {(c.skills || []).map((s) => <span key={s} className="text-xs bg-white/5 text-white/70 rounded-md px-2 py-0.5">{s}</span>)}
              </div>
              {(c.industries || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(c.industries || []).map((s) => <span key={s} className="text-xs bg-[#66B2FF]/10 text-[#66B2FF] rounded-md px-2 py-0.5">{s}</span>)}
                </div>
              )}
              {(c.certifications || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-white/45 text-xs mb-1">Certifications</div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.certifications.map((s, i) => <span key={i} className="text-xs bg-white/5 text-white/70 rounded-md px-2 py-0.5">{s}</span>)}
                  </div>
                </div>
              )}
            </div>

            {/* Profile / contact info */}
            <div className="rounded-xl border border-white/10 p-4 bg-[#0A0F1E]">
              <SectionTitle icon={Briefcase}>Candidate Profile</SectionTitle>
              <div className="space-y-2">
                <DetailField label="Email" value={c.email} icon={Mail} />
                <DetailField label="Phone" value={c.phone} icon={Phone} />
                <DetailField label="Location" value={[location, c.time_zone].filter(Boolean).join(" \u00b7 ")} icon={MapPin} />
                <DetailField label="Current Role" value={c.current_position} icon={Briefcase} />
                <DetailField label="Desired Role" value={c.desired_positions} icon={Target} />
                <DetailField label="Experience" value={c.years_experience != null ? `${c.years_experience} yr` : ""} icon={Clock} />
                <DetailField label="Education" value={c.highest_education} icon={GraduationCap} />
                <DetailField label="English" value={c.english_proficiency} icon={Globe} />
                <DetailField label="Languages" value={(c.additional_languages || []).join(", ")} icon={Globe} />
                <DetailField label="Preference" value={c.employment_preference} icon={Building2} />
                <DetailField label="Desired Pay" value={c.desired_salary ? `${c.salary_currency || ""} ${Number(c.desired_salary).toLocaleString()}/mo` : ""} icon={DollarSign} />
                <DetailField label="Availability" value={c.availability} icon={Users} />
              </div>
              <div className="flex gap-3 pt-3 mt-3 border-t border-white/10">
                {normalizeUrl(c.linkedin) && <a href={normalizeUrl(c.linkedin)} target="_blank" rel="noreferrer" className="text-[#66B2FF] text-xs hover:underline">LinkedIn</a>}
                {normalizeUrl(c.portfolio) && <a href={normalizeUrl(c.portfolio)} target="_blank" rel="noreferrer" className="text-[#66B2FF] text-xs hover:underline">Portfolio</a>}
                {normalizeUrl(c.website) && <a href={normalizeUrl(c.website)} target="_blank" rel="noreferrer" className="text-[#66B2FF] text-xs hover:underline">Website</a>}
              </div>
            </div>
          </div>
        </div>
      )}
    </WorkspaceShell>
  );
};

export default CandidateWorkspace;
